import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CalendarIcon, Clock, User, Mail, Phone, FileText } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const sessionTypes = [
  { value: "wedding", label: "Wedding", price: "Starting at $2,600" },
  { value: "senior", label: "Senior Portraits", price: "Starting at $475" },
  { value: "family", label: "Family Session", price: "Starting at $350" },
  { value: "boudoir", label: "Boudoir Session", price: "Starting at $375" },
  { value: "branding", label: "Branding Session", price: "Starting at $400" },
  { value: "maternity", label: "Maternity Session", price: "Starting at $375" },
  { value: "couples", label: "Couples Session", price: "Starting at $375" },
  { value: "newborn", label: "Lifestyle Newborn", price: "Starting at $500" },
  { value: "consultation", label: "Free Consultation", price: "Free" },
];

const packagesBySessionType = {
  wedding: [
    "The Essential Package - $2,600",
    "The Signature Package - $3,500",
    "The Luxury Package - $4,200"
  ],
  senior: [
    "Package 1 - $475",
    "Package 2 - $525",
    "Package 3 - $575"
  ],
  family: [
    "Classic Session - $350",
    "Essential Session - $425",
    "Extended Session - $600"
  ],
  boudoir: [
    "The Tease - $375",
    "The Reveal - $475",
    "The Full Experience - $625"
  ],
  branding: [
    "Starter Brand - $400",
    "Full Brand Story - $600"
  ],
  maternity: [
    "Package 1 - $375",
    "Package 2 - $425"
  ],
  couples: [
    "Mini Session - $375",
    "Full Session - $425"
  ],
  newborn: [
    "Petite Session - $500",
    "Full Lifestyle Session - $550"
  ],
};

const BookingSection = ({ isVisible }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState(null);
  const [availableTimes, setAvailableTimes] = useState([]);
  const [isLoadingTimes, setIsLoadingTimes] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    session_type: "",
    package_name: "",
    preferred_time: "",
    notes: "",
  });

  // Fetch available times when date changes
  useEffect(() => {
    const fetchAvailableTimes = async () => {
      if (!date) return;
      
      setIsLoadingTimes(true);
      try {
        const formattedDate = format(date, "yyyy-MM-dd");
        const response = await axios.get(`${API}/available-times?date=${formattedDate}`);
        setAvailableTimes(response.data.available_times);
      } catch (error) {
        console.error("Error fetching available times:", error);
        setAvailableTimes([
          "9:00 AM", "10:00 AM", "11:00 AM",
          "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"
        ]);
      } finally {
        setIsLoadingTimes(false);
      }
    };

    fetchAvailableTimes();
  }, [date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    if (name === "session_type") {
      setFormData((prev) => ({ ...prev, session_type: value, package_name: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.session_type || !date || !formData.preferred_time) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (formData.session_type !== "consultation" && !formData.package_name) {
      toast.error("Please select a package");
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        ...formData,
        preferred_date: format(date, "yyyy-MM-dd"),
      };
      
      await axios.post(`${API}/booking`, bookingData);
      
      // Send to studio manager webhook
      fetch('https://studio-manager-50.preview.emergentagent.com/api/webhook/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          session_type: formData.session_type,
          package_name: formData.package_name,
          preferred_date: format(date, "yyyy-MM-dd"),
          message: formData.notes
        })
      });

      toast.success("Booking request submitted! I'll confirm your session soon.");
      
      setFormData({
        name: "",
        email: "",
        phone: "",
        session_type: "",
        package_name: "",
        preferred_time: "",
        notes: "",
      });
      setDate(null);
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="booking"
      className="py-24 lg:py-32 bg-[#F5F2EB]"
      data-testid="booking-section"
    >
      <div className="max-w-5xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div
          className={`text-center mb-16 ${
            isVisible ? "animate-fade-in-up" : "opacity-0"
          }`}
        >
          <p className="overline text-[#C8A99C] mb-4">Schedule Your Session</p>
          <h2 className="font-heading text-4xl lg:text-5xl font-light tracking-tight text-[#2C2A28] mb-6">
            Book Now
          </h2>
          <div className="section-divider mx-auto mb-6" />
          <p className="font-body text-[#5C5A56] max-w-2xl mx-auto">
            Ready to create magic? Select your preferred date and time, and I'll reach out to confirm all the details.
          </p>
        </div>

        {/* Booking Form */}
        <div
          className={`bg-[#FDFBF9] p-8 lg:p-12 ${
            isVisible ? "animate-fade-in-up animation-delay-200" : "opacity-0"
          }`}
        >
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="booking-form">
            {/* Personal Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56] flex items-center gap-2">
                  <User size={14} />
                  Full Name *
                </Label>
                <Input
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                  data-testid="booking-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56] flex items-center gap-2">
                  <Mail size={14} />
                  Email *
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                  data-testid="booking-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56] flex items-center gap-2">
                  <Phone size={14} />
                  Phone *
                </Label>
                <Input
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                  data-testid="booking-phone-input"
                />
              </div>
            </div>

            {/* Session Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56]">
                  Session Type *
                </Label>
                <Select
                  value={formData.session_type}
                  onValueChange={(value) => handleSelectChange("session_type", value)}
                >
                  <SelectTrigger
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="booking-session-type-select"
                  >
                    <SelectValue placeholder="Select a session type" />
                  </SelectTrigger>
                  <SelectContent>
                    {sessionTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        <div className="flex justify-between items-center w-full">
                          <span>{type.label}</span>
                          <span className="text-[#5C5A56] text-sm ml-4">{type.price}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56]">
                  Package {formData.session_type && formData.session_type !== "consultation" ? "*" : ""}
                </Label>
                <Select
                  value={formData.package_name}
                  onValueChange={(value) => handleSelectChange("package_name", value)}
                  disabled={!formData.session_type || formData.session_type === "consultation"}
                >
                  <SelectTrigger
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="booking-package-select"
                  >
                    <SelectValue placeholder={!formData.session_type ? "Select a session type first" : "Select a package"} />
                  </SelectTrigger>
                  <SelectContent>
                    {(packagesBySessionType[formData.session_type] || []).map((pkg) => (
                      <SelectItem key={pkg} value={pkg}>
                        {pkg}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56] flex items-center gap-2">
                  <CalendarIcon size={14} />
                  Preferred Date *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="w-full flex items-center justify-between px-4 py-2 bg-transparent border border-[#E8E4DD] text-left font-body text-sm hover:border-[#C8A99C] transition-colors"
                      data-testid="booking-date-picker"
                    >
                      {date ? format(date, "MMMM d, yyyy") : "Select a date"}
                      <CalendarIcon size={16} className="text-[#5C5A56]" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label className="font-body text-sm text-[#5C5A56] flex items-center gap-2">
                  <Clock size={14} />
                  Preferred Time *
                </Label>
                <Select
                  value={formData.preferred_time}
                  onValueChange={(value) => handleSelectChange("preferred_time", value)}
                  disabled={!date || isLoadingTimes}
                >
                  <SelectTrigger
                    className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none"
                    data-testid="booking-time-select"
                  >
                    <SelectValue placeholder={isLoadingTimes ? "Loading..." : "Select a time"} />
                  </SelectTrigger>
                  <SelectContent>
                    {availableTimes.map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="font-body text-sm text-[#5C5A56] flex items-center gap-2">
                <FileText size={14} />
                Additional Notes
              </Label>
              <Textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Tell me about your vision, preferred locations, special requests, or any questions you have..."
                rows={4}
                className="bg-transparent border-[#E8E4DD] focus:border-[#C8A99C] rounded-none resize-none"
                data-testid="booking-notes-input"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-[#2C2A28] text-[#FDFBF9] font-body text-sm tracking-wide hover:bg-[#5C5A56] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="booking-form-submit"
            >
              {isSubmitting ? "Submitting..." : "Request Booking"}
            </button>

            <p className="text-center font-body text-xs text-[#5C5A56]">
              * A non-refundable deposit is required to confirm your booking. I'll reach out within 24-48 hours to finalize details.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;
